import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { ApiError } from '@aithora/shared';
import { ErrorNote } from '../../ErrorNote';
import { useReadOnlyReason } from '../../ui/ReadOnly';
import { sx } from '../../ui/designStyles';

/**
 * Settings' confirm-first dialog, copied from the design's own (seAsk, T8625-8638): the
 * section in red capitals, the question, what happens, the history line, then "Keep as is"
 * and the red action. It keeps the shared ConfirmDialog's behaviour: focus inside the
 * dialog (Tab reaches Keep as is first), Escape closes, the action runs once, an error stays in the dialog, and a
 * read-only session cannot confirm. The caller adds confirm:true to its write.
 */
export function AskDialog({
  sec,
  title,
  body,
  extra,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  sec: string;
  title: string;
  body: ReactNode;
  extra?: ReactNode;
  confirmLabel: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const roReason = useReadOnlyReason();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    // Focus the dialog itself (no ring, as the design draws it); Tab reaches Keep as is first.
    boxRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const go = async () => {
    if (busy || roReason) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setError(e as ApiError);
      setBusy(false);
    }
  };
  return (
    <div role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()} style={sx('position:fixed;inset:0;z-index:80;background:rgba(34,39,46,.32);display:flex;align-items:center;justify-content:center;padding:20px;')}>
      <div ref={boxRef} tabIndex={-1} role="alertdialog" aria-modal="true" aria-labelledby={`${id}-t`} style={sx('outline:none;width:100%;max-width:440px;background:#FCFAF4;border-radius:12px;padding:20px 22px;box-shadow:0 18px 48px rgba(34,39,46,.28);display:flex;flex-direction:column;gap:10px;')}>
        <span style={sx('font-size:14px;letter-spacing:.12em;text-transform:uppercase;font-weight:700;color:#B3261E;')}>{sec}</span>
        <span id={`${id}-t`} style={sx('font-size:18px;font-weight:800;color:#22272E;letter-spacing:-.01em;')}>{title}</span>
        <span style={sx('font-size:16px;color:#3C454F;line-height:1.55;text-wrap:pretty;')}>{body}</span>
        {extra}
        <span style={sx('font-size:16px;color:#54606C;')}>This is recorded in the section&apos;s change history.</span>
        {error && <ErrorNote error={error} />}
        <div style={sx('display:flex;align-items:center;justify-content:flex-end;gap:10px;flex-wrap:wrap;margin-top:6px;')}>
          <a
            href="#"
            role="button"
            onClick={(e) => { e.preventDefault(); onClose(); }}
            style={sx('display:inline-flex;align-items:center;min-height:44px;padding:0 16px;border-radius:8px;font-size:16px;font-weight:700;color:#22272E;text-decoration:none;border:1px solid rgba(34,39,46,.2);')}
          >
            Keep as is
          </a>
          <a
            href="#"
            role="button"
            aria-disabled={busy || !!roReason || undefined}
            title={roReason ?? undefined}
            onClick={(e) => { e.preventDefault(); void go(); }}
            style={sx('display:inline-flex;align-items:center;min-height:44px;padding:0 18px;border-radius:8px;font-size:16px;font-weight:700;color:#fff;background:#B3261E;text-decoration:none;')}
          >
            {busy ? 'Sending…' : confirmLabel}
          </a>
        </div>
      </div>
    </div>
  );
}
